<div class="h-full">
  <ng-container *ngIf="step === 'dynamic-pricing-preview'">
    <div class="h-full padding-children flex flex-col">
      <div class="flex-1 flex flex-col gap-6">
        <div>
          <div class="flex items-center gap-1 mb-3 cursor-pointer" (click)="goBack()">
            <sbnb-icon icon="small-arrow-left" [size]="20"></sbnb-icon>

            <div class="text-sm m-0">Property settings</div>
          </div>
          <h3 class="text-lg font-semibold mb-2">Dynamic pricing can boost revenue by over 20%*</h3>
          <p class="text-small m-0">
            *Based on market data from 20M+ listings, top data partners, and machine learning to deliver optimized
            pricing.
          </p>
        </div>
        <p class="text-night m-0 text-sm">
          <strong>How?</strong>
          It reacts to market demand to boost revenue and increase occupancy. Hover on the calendar to see how pricing
          works.
        </p>

        <div>
          <p class="text-night m-1 text-sm">
            <strong>Set a base price preference to see how it affects your nightly prices</strong>
          </p>

          <div class="flex flex-col gap-3">
            <ng-container *ngTemplateOutlet="pricingPreferenceOption"></ng-container>
          </div>
        </div>

        <div>
          <h4 class="text-sm font-semibold mb-3">What the colors mean</h4>
          <div class="flex flex-col gap-3">
            <div class="flex items-start gap-3">
              <span class="text-grass-darker text-lg">$</span>
              <span class="text-sm leading-7">You could earn more by raising your price.</span>
            </div>
            <div class="flex items-start gap-3">
              <span class="text-lg">$</span>
              <span class="text-sm leading-7">Your price looks good, it matches our recommendation.</span>
            </div>
            <div class="flex items-start gap-3">
              <span class="text-rose-darker text-lg">$</span>
              <span class="text-sm leading-7">You could get more bookings by lowering your price.</span>
            </div>
          </div>
        </div>
      </div>
      <div class="sticky-footer">
        <p
          class="text-rose-darker text-sm text-center m-0"
          *ngIf="$dynamicPricingOptions()?.data_errors | fn: hasErrors">
          The selected property is currently not eligible for dynamic pricing.
        </p>
        <ng-container *ngIf="!(canUseDynamicPricing$ | async); else customizeCta">
          <div *ngIf="!(isPrimaryUser | async); else updateSection">
            <h4 class="text-sm font-semibold">Contact your team admin</h4>
            <p class="text-sm m-0 mb-2 !leading-6">
              You don't have sufficient permissions to enable dynamic pricing, please get in touch with your team admin in order to proceed.
            </p>
          </div>

          <ng-template #updateSection>
            <div *ngIf="isLegacy$ | async" class="flex flex-col">
              <h4 class="text-sm font-semibold">Unlock with a plan upgrade</h4>
              <p class="text-sm m-0 mb-2 !leading-6">
                Dynamic pricing is included on Host, Professional, and Mogul plans.
              </p>
              <sbnb-button [fullWidth]="true" [label]="'Upgrade my plan'" (click)="visitBilling()"></sbnb-button>
            </div>

            <div *ngIf="isTrial$ | async" class="flex flex-col">
              <h4 class="text-sm font-semibold">Unlock by adding a payment method</h4>
              <p class="text-sm m-0 mb-2 !leading-6">
                We couldn't find an active payment method linked to your account, you need to add least one to unlock
                Dynamic pricing.
              </p>
              <sbnb-button [fullWidth]="true" [label]="'Add a payment method'" (click)="visitBilling()"></sbnb-button>
            </div>

            <!-- todo fallback message -->
          </ng-template>
        </ng-container>
        <ng-template #customizeCta>
          <sbnb-button
            [disabled]="!(canUseDynamicPricing$ | async) || ($dynamicPricingOptions()?.data_errors | fn: hasErrors)"
            [fullWidth]="true"
            [label]="'Customize setup'"
            (click)="setStep('dynamic-pricing-settings', $dynamicPricingOptions()?.data_errors)"></sbnb-button>
        </ng-template>
      </div>
    </div>
  </ng-container>

  <ng-container *ngIf="step === 'dynamic-pricing-settings'">
    <form [formGroup]="dynamicPricingForm" class="h-full padding-children flex flex-col">
      <div class="flex-1 flex flex-col gap-6">
        <div>
          <div class="flex items-center gap-1 mb-3">
            <sbnb-icon
              (click)="mode === 'setup' ? (step = 'dynamic-pricing-preview') : goBack()"
              icon="small-arrow-left"
              [size]="20"></sbnb-icon>

            <div class="text-sm m-0">{{ mode === 'setup' ? 'Pricing preview' : 'Pricing settings' }}</div>
          </div>
          <h3 class="text-lg font-semibold mb-2">{{ mode === 'setup' ? 'Setup' : 'Update' }} dynamic pricing</h3>
          <p class="text-small m-0">Simply set a base, min and max price, and change them any time</p>
        </div>

        <div>
          <p class="text-night m-1 text-sm">
            <strong>Base price</strong>
          </p>

          <div class="flex flex-col gap-3">
            <ng-container *ngTemplateOutlet="pricingPreferenceOption"></ng-container>
          </div>
        </div>
        <div class="mt-3">
          <u
            class="text-night cursor-pointer"
            *ngIf="dynamicPricingForm.value.pricingPreference !== PRICING_PREFERENCE.CUSTOM"
            (click)="setPricingPreference(PRICING_PREFERENCE.CUSTOM, dynamicPricingForm.value.basePrice != null)">
            Set custom base price
          </u>
          <u
            class="text-night cursor-pointer"
            *ngIf="dynamicPricingForm.value.pricingPreference === PRICING_PREFERENCE.CUSTOM"
            (click)="setPricingPreference(undefined, false)">
            Hide custom base price
          </u>

          <ng-container *ngIf="dynamicPricingForm.value.pricingPreference === PRICING_PREFERENCE.CUSTOM">
            <div class="flex justify-start gap-1 flex-col mt-1">
              <span fxFlex class="text-sm strong">Custom base price</span>
              <span class="text-sm">
                Our dynamic base prices shift with demand. Choose a custom price if you prefer a fixed, unchanging rate.
              </span>
            </div>

            <mat-form-field class="fw">
              <span matPrefix class="prefix-bg">
                <span class="text-sm pr-3">{{ property.currency }}</span>
              </span>
              <input matInput formControlName="basePrice" type="number" />
            </mat-form-field>
          </ng-container>
        </div>
        <div>
          <div fxLayout fxLayoutAlign="start center" fxLayoutGap="12px">
            <span fxFlex class="text__xsmall strong">Minimum price (optional)</span>
          </div>

          <mat-form-field class="fw">
            <span matPrefix class="prefix-bg">
              <span class="text__small pr-3">{{ property.currency }}</span>
            </span>
            <input matInput formControlName="minPrice" type="number" />
          </mat-form-field>

          <div fxLayout fxLayoutAlign="start center" fxLayoutGap="12px">
            <span fxFlex class="text__xsmall strong">Maximum price (optional)</span>
          </div>

          <mat-form-field class="fw">
            <span matPrefix class="prefix-bg">
              <span class="text__small pr-3">{{ property.currency }}</span>
            </span>
            <input matInput formControlName="maxPrice" type="number" />
          </mat-form-field>

          <div class="text-rose-darker text-sm mt-1" *ngIf="dynamicPricingForm.hasError('minGreaterThanMax')">
            Min price cannot be greater than max price.
          </div>
          <div class="text-rose-darker text-sm mt-1" *ngIf="dynamicPricingForm.hasError('minGreaterThanBasePrice')">
            Min price cannot be greater than base price.
          </div>
          <div class="text-rose-darker text-sm mt-1" *ngIf="dynamicPricingForm.hasError('maxLessThanBasePrice')">
            Max price cannot be less than base price.
          </div>
          <p class="text-sm !leading-6 m-0 mt-1">
            *Guests could see lower or higher prices if you have discounts, promotions, or markups.
          </p>
        </div>
      </div>

      <div class="sticky-footer">
        <sbnb-button
          *ngIf="mode === 'setup'"
          [disabled]="dynamicPricingForm.invalid"
          [fullWidth]="true"
          [label]="'Continue'"
          (click)="presentDynamicPricingModal()"></sbnb-button>

        <sbnb-button
          *ngIf="mode === 'update'"
          [loading]="updateLoading"
          [disabled]="dynamicPricingForm.invalid || !dynamicPricingForm.dirty"
          [fullWidth]="true"
          [label]="'Update'"
          (click)="updateDynamicPricing()"></sbnb-button>
        <sbnb-button
          *ngIf="$dynamicPricingOn()"
          [fullWidth]="true"
          [label]="'Turn off dynamic pricing'"
          [type]="'secondary'"
          (click)="presentDynamicPricingDeActivateModal()"></sbnb-button>
      </div>
    </form>
  </ng-container>
</div>

<ng-template #pricingPreferenceOption>
  <div
    *ngFor="let preference of pricingPreferences"
    class="flex items-center gap-3 p-4 justify-between rounded border cursor-pointer hover:opacity-85"
    [ngClass]="{
      'border-grass-darker bg-grass-lighter': preference.value === dynamicPricingForm.value.pricingPreference,
      'border-smoke-darker ': preference.value !== dynamicPricingForm.value.pricingPreference
    }"
    (click)="setPricingPreference(preference.value)">
    <span
      [class.text-grass-darker]="preference.value === dynamicPricingForm.value.pricingPreference"
      [class.text-night]="preference.value !== dynamicPricingForm.value.pricingPreference">
      {{ preference.label }}
    </span>
    <span *ngIf="preference.value !== dynamicPricingForm.value.pricingPreference && preference.percentageDifference">
      {{ preference.percentageDifference }}%
    </span>
    <sbnb-icon
      *ngIf="preference.value === dynamicPricingForm.value.pricingPreference"
      icon="check"
      [size]="20"
      class="text-grass-darker"></sbnb-icon>
  </div>
</ng-template>
